import { useState, useEffect, useRef } from 'react';
import type { Question, AnswerValue } from '../types';
import { ANSWER_OPTIONS } from '../data/questions';
import { CATEGORIES } from '../data/questions';
import QuestionInfo from './QuestionInfo';
import { replacePartnerPlaceholder } from '../lib/resultsDisplay';

interface QuestionCardProps {
  question: Question;
  currentAnswer: AnswerValue | null;
  onAnswer: (questionId: string, answer: AnswerValue) => void;
  partnerName: string;
}

const CONFIRM_MS = 50;
const EXIT_ANIMATION_MS = 150;

export default function QuestionCard({
  question,
  currentAnswer,
  onAnswer,
  partnerName,
}: QuestionCardProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<AnswerValue | null>(currentAnswer);
  const [animationState, setAnimationState] = useState<'enter' | 'idle' | 'exit'>('enter');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const confirmTimeoutRef = useRef<number | null>(null);
  const submitTimeoutRef = useRef<number | null>(null);

  const categoryMeta = CATEGORIES.find((c) => c.id === question.category);

  function clearPendingTimers() {
    if (confirmTimeoutRef.current !== null) {
      window.clearTimeout(confirmTimeoutRef.current);
      confirmTimeoutRef.current = null;
    }
    if (submitTimeoutRef.current !== null) {
      window.clearTimeout(submitTimeoutRef.current);
      submitTimeoutRef.current = null;
    }
  }

  // Reset animation on question change
  useEffect(() => {
    clearPendingTimers();
    setIsTransitioning(false);
    setSelectedAnswer(currentAnswer);
    setAnimationState('enter');
    const t = requestAnimationFrame(() => {
      requestAnimationFrame(() => setAnimationState('idle'));
    });
    return () => {
      clearPendingTimers();
      cancelAnimationFrame(t);
    };
  }, [question.id, currentAnswer]);

  function handleSelect(value: AnswerValue) {
    if (isTransitioning) return;

    setIsTransitioning(true);
    setSelectedAnswer(value);

    confirmTimeoutRef.current = window.setTimeout(() => {
      setAnimationState('exit');
      submitTimeoutRef.current = window.setTimeout(() => {
        onAnswer(question.id, value);
      }, EXIT_ANIMATION_MS);
    }, CONFIRM_MS);
  }

  const translateClass =
    animationState === 'enter'
      ? 'translate-x-[60px] opacity-0'
      : animationState === 'exit'
        ? '-translate-x-[60px] opacity-0'
        : 'translate-x-0 opacity-100';

  return (
    <div
      className={`transition-all duration-150 ease-out ${translateClass}`}
    >
      <div className="bg-purple-card border border-white/10 rounded-[2.5rem] p-6 sm:p-8 shadow-2xl relative overflow-hidden max-w-lg mx-auto">
        {/* Decorative background glow */}
        <div className="absolute -top-24 -right-24 w-48 h-48 bg-primary/10 blur-[80px] rounded-full pointer-events-none" />

        <div className="relative z-10 text-center">
          {/* Category pill */}
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 mb-6">
            <span className="text-sm">{categoryMeta?.emoji}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">{categoryMeta?.label}</span>
          </div>

          {/* Question text */}
          <h2 className="text-2xl font-extrabold text-white text-center mb-10 min-h-[3.5rem] flex items-center justify-center">
            {replacePartnerPlaceholder(question.text, partnerName)}
          </h2>

          {/* Info tooltip (if available) */}
          {question.info && (
            <div className="-mt-6 mb-6">
              <QuestionInfo info={question.info} variant="dark" />
            </div>
          )}

          {/* Answer buttons */}
          <div className="grid grid-cols-4 gap-1.5 sm:gap-2 mb-3">
            {ANSWER_OPTIONS.map((opt) => {
              const isSelected = selectedAnswer === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => handleSelect(opt.value)}
                  disabled={isTransitioning}
                  className={`
                    flex flex-col items-center justify-center
                    w-full aspect-square rounded-2xl
                    text-2xl sm:text-4xl
                    transition-all duration-150 active:scale-90 disabled:cursor-not-allowed
                    ${
                      isSelected
                        ? 'bg-primary/20 border-2 border-primary shadow-[0_0_15px_rgba(255,51,102,0.2)]'
                        : 'answer-option-unselected'
                    }
                  `}
                  aria-label={opt.label}
                >
                  {opt.emoji}
                </button>
              );
            })}
          </div>

          {/* Labels row */}
          <div className="grid grid-cols-4 gap-2">
            {ANSWER_OPTIONS.map((opt) => {
              const isSelected = selectedAnswer === opt.value;
              return (
                <span
                  key={opt.value}
                  className={`text-center text-xs sm:text-base font-semibold leading-tight ${isSelected ? 'text-primary' : 'text-slate-500'}`}
                >
                  {opt.label}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
